import { api } from '../../lib/api.js';
import { getAppConfig } from '../../lib/app-config.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html, nothing } from '../../lib/lit.js';
import { portalPollUrl, promptSummary, STATE_BADGE, tallyRows } from '../../lib/polls.js';
import '../app/share-link.js';

// A poll's results: tallies per choice question and every written answer —
// free-text questions and "Not listed" entries alike — with the respondent's
// handle. Handles only: d3-community returns nothing else, and nothing here
// looks a person up.
class PollResultsView extends BaseComponent {
    static properties = {
        pollId: { type: String, attribute: 'poll-id' },
        /** @type {object|null} */
        _poll: { state: true },
        /** @type {object|null} */
        _results: { state: true },
        _loading: { state: true },
        _error: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 56rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .answer {
            white-space: pre-wrap;
        }
    `;

    constructor() {
        super();
        this.pollId = '';
        this._poll = null;
        this._results = null;
        this._loading = true;
        this._error = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const [pollBody, results] = await Promise.all([
                api.get(`/polls/${this.pollId}`),
                api.get(`/polls/${this.pollId}/results`),
            ]);
            this._poll = pollBody.poll;
            this._results = results;
        } catch (error) {
            this._error = error.status === 404 ? 'No such poll.' : error.message;
        } finally {
            this._loading = false;
        }
    }

    render() {
        if (this._loading) return html`<div class="wrap"><p class="text-muted">Loading…</p></div>`;
        if (this._error) {
            return html`<div class="wrap">
                <div class="alert alert-danger" role="alert">${this._error}</div>
                <a href="/polls">All polls</a>
            </div>`;
        }

        const poll = this._poll;
        const results = this._results;
        const byId = Object.fromEntries(results.questions.map((q) => [q.questionId, q]));
        return html`
            <div class="wrap">
                <a class="small" href="/polls/${poll.id}"><i class="bi bi-arrow-left"></i> ${poll.title || 'Untitled'}</a>
                <div class="d-flex align-items-center gap-2 mt-2 mb-1">
                    <h1 class="h4 mb-0">Results</h1>
                    <span class="badge ${STATE_BADGE[poll.state]}">${poll.state}</span>
                    ${
                        poll.state !== 'draft'
                            ? html`<d3-share-link
                                  class="ms-2"
                                  href=${portalPollUrl(getAppConfig().communityPortalUrl, poll.id)}
                              ></d3-share-link>`
                            : nothing
                    }
                    <button
                        class="btn btn-sm btn-outline-secondary ms-auto"
                        type="button"
                        @click=${this._load}
                    >
                        Refresh
                    </button>
                </div>
                <p class="text-muted">
                    ${results.respondents} ${results.respondents === 1 ? 'person has' : 'people have'}
                    answered${poll.state === 'open' ? ' so far' : ''}.
                    ${poll.state === 'draft' ? 'This poll is a draft; nobody can answer it yet.' : nothing}
                </p>

                ${poll.questions.map((question, index) =>
                    byId[question.id]
                        ? this._renderQuestion(question, byId[question.id], index)
                        : nothing,
                )}
            </div>
        `;
    }

    _renderQuestion(question, result, index) {
        return html`
            <div class="card mb-3">
                <div class="card-body">
                    <h2 class="h6 mb-1">${index + 1}. ${promptSummary(question.prompt)}</h2>
                    <div class="text-muted small mb-3">
                        ${result.answered} answered
                        ${question.kind === 'choice' && question.allowMultiple ? '· pick any' : nothing}
                    </div>
                    ${question.kind === 'choice' ? this._renderTallies(question, result) : nothing}
                    ${this._renderTexts(question, result)}
                </div>
            </div>
        `;
    }

    _renderTallies(question, result) {
        return tallyRows(question, result).map(
            (row) => html`
                <div class="small d-flex justify-content-between">
                    <span class=${row.notListed ? 'fst-italic' : ''}>${row.label}</span>
                    <span class="text-muted">${row.count} · ${row.percent}%</span>
                </div>
                <div
                    class="progress mb-2"
                    style="height: 0.5rem;"
                    role="progressbar"
                    aria-label=${row.label}
                    aria-valuenow=${row.percent}
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    <div class="progress-bar" style="width: ${row.percent}%"></div>
                </div>
            `,
        );
    }

    _renderTexts(question, result) {
        if (result.texts.length === 0) {
            return question.kind === 'text'
                ? html`<p class="text-muted small mb-0"><em>No written answers yet.</em></p>`
                : nothing;
        }
        return html`
            ${
                question.kind === 'choice'
                    ? html`<div class="small fw-semibold mt-3 mb-1">Not listed</div>`
                    : nothing
            }
            <div class="list-group list-group-flush">
                ${result.texts.map(
                    (answer) => html`
                        <div class="list-group-item px-0">
                            <span class="text-muted small">${answer.handle}</span>
                            <div class="answer">${answer.text}</div>
                        </div>
                    `,
                )}
            </div>
        `;
    }
}

customElements.define('d3-poll-results-view', PollResultsView);
